import { Form, Formik } from 'formik'
import { Icon } from '@iconify/react'
import {
  Button,
  Checkbox,
  FormikSelectField,
  FormikTextField,
  Modal,
  Tooltip,
} from '@pepper/ui'

import editTopicSchema from './schema'

import s from './EditTopicForm.module.css'

export type EditFormValues = {
  name: string
  primaryKeyword?: string
  secondaryKeywords?: string[]
  shouldAttachSeoGuidelines?: boolean
}

type Props = {
  open: boolean
  initialValues: EditFormValues
  onClose: () => void
  onSubmit: (values: EditFormValues) => void | Promise<void>
  isLoading?: boolean
  title?: string
  isSuggestedTopic?: boolean
  showAttachSeoGuidelines?: boolean
}

const EditTopicForm = ({
  open,
  onClose,
  initialValues,
  onSubmit,
  isLoading,
  isSuggestedTopic,
  title,
  showAttachSeoGuidelines,
}: Props) => {
  return (
    <Modal
      title={title ?? 'Edit Topic'}
      visible={open}
      footer={null}
      onCancel={onClose}
      destroyOnClose={true}
    >
      <Formik
        validationSchema={editTopicSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={onSubmit}
      >
        {({ isValid, values, setFieldValue }): React.ReactNode => {
          const isDirty =
            initialValues.name !== values.name ||
            initialValues.primaryKeyword !== values.primaryKeyword ||
            initialValues.secondaryKeywords?.join(',') !==
              values.secondaryKeywords?.join(',')
          return (
            <Form className={s.wrapper}>
              <FormikTextField
                label="Topic"
                name="name"
                required={true}
                fieldProps={{
                  required: true,
                  placeholder: 'Topic',
                }}
              />
              <FormikTextField
                name="primaryKeyword"
                className={s.select}
                required={true}
                label={<span className={s.label}>Primary Keyword&nbsp;</span>}
                fieldProps={{
                  placeholder: 'Enter Primary Keyword',
                }}
              />
              <FormikSelectField
                name="secondaryKeywords"
                className={s.select}
                label={
                  <span className={s.label}>Secondary Keywords&nbsp;</span>
                }
                fieldProps={{
                  open: false,
                  mode: 'tags',
                  tokenSeparators: [','],
                  placeholder: 'Enter Secondary Keywords',
                  textFormat: (value: string[]) => {
                    return value.filter(v => v.trim())
                  },
                  onKeyDown: e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                    }
                  },
                }}
              />

              {(isSuggestedTopic || showAttachSeoGuidelines) && (
                <div className={s.seoGuidelines}>
                  <Checkbox
                    checked={values.shouldAttachSeoGuidelines}
                    onChange={e =>
                      setFieldValue(
                        'shouldAttachSeoGuidelines',
                        e.target.checked
                      )
                    }
                  >
                    Create SEO Content Guidelines for Topic
                  </Checkbox>
                  <Tooltip
                    placement="bottom"
                    title="Guidelines include best practices in terms of SEO, readability and
          more from top ranking pages."
                  >
                    <Icon icon="lucide:info" width={14} height={14} />
                  </Tooltip>
                </div>
              )}

              <div className={s.footer}>
                <Button type="link" htmlType="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  disabled={!isValid || !isDirty}
                >
                  {isSuggestedTopic ? 'Update and Add Topic' : 'Update Topic'}
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default EditTopicForm
